'use client'

import { FormRadio, FormRadioGroup, useFormContext, useStoreState } from '@ariakit/react'
import capitalize from 'lodash-es/capitalize'
import { useCallback, useContext, useMemo } from 'react'

import { Select, SelectOption, TypeStyleHeadlineMicro } from '@syconium/little-miss-figgy'

import { trackEvent } from '../../../lib/analytics'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import { FormError } from '../form/FormError'

import {
  VariantSelectionFieldProps,
  VariantSelectionFieldValueDisplay,
  VariantSelectionFormContext,
  VariantSelectionFormGroupLabel,
  VariantSelectionFormState,
  VariantSelectionFromSoldOutMessage,
  VariantSelectionInvisibleInput,
  VariantSelectionOption,
  VariantSelectionOptionsRow,
} from './VariantSelectionForm'

const useVariantSelectionFitField = () => {
  const { selectedVariant, product, variantGroup } = useContext(VariantSelectionFormContext)!
  const translations = useTranslation()
  const formContext = useFormContext()
  const value = formContext!.useValue<VariantSelectionFormState['fit']>('fit') ?? ''
  const option = product.options.fits?.find(fitOption => fitOption.fit.id === value)
  const displayValue = option?.fit.displayValue ?? value
  const hide = displayValue.length > 0 && product.options.fits?.length === 1
  const hasASizeInStock = !!product.options.sizes?.find(size => size.outOfStock === false)
  const renderSoldOutLabel =
    option?.outOfStock && variantGroup.availability === 'AVAILABLE' && !hasASizeInStock

  if (value.length === 0 && selectedVariant?.fit?.id) {
    formContext?.setValue('fit', selectedVariant.fit.id)
  }

  const soldOutLabelPostfix = useMemo(() => {
    return renderSoldOutLabel ? (
      <>
        &nbsp;
        <VariantSelectionFromSoldOutMessage />
      </>
    ) : null
  }, [renderSoldOutLabel])

  const setValue = useCallback(
    (newFit: string) => {
      formContext!.setValue('fit', newFit)
    },
    [formContext]
  )

  const error = useStoreState(formContext, () => formContext?.getError('fit'))

  const renderAsDisplayValue = useCallback(
    (valueToRender: string) => {
      const valueIsSelected = valueToRender === value
      const valueAsFitOption = product.options.fits?.find(
        fitOption => fitOption.fit.id === valueToRender
      )
      const valueIsOutOfStock = valueAsFitOption?.outOfStock === true
      const renderAsOutOfStock = valueIsSelected
        ? valueIsOutOfStock && !hasASizeInStock
        : valueIsOutOfStock
      const valueAsDisplayText = valueAsFitOption?.fit.displayValue ?? valueToRender
      const fitDisplayText = renderAsOutOfStock
        ? `${capitalize(valueAsDisplayText)} ${translations.giftCard.dropdownSoldOutOptionText}`
        : capitalize(valueAsDisplayText)
      return <TypeStyleHeadlineMicro>{fitDisplayText}</TypeStyleHeadlineMicro>
    },
    [hasASizeInStock, product.options.fits, translations.giftCard.dropdownSoldOutOptionText, value]
  )

  const labelText =
    variantGroup.primaryCategory.key === 'SCRUB_TOP'
      ? translations.pages.product.scrubTopsFitTitle
      : variantGroup.primaryCategory.key === 'SCRUB_PANTS'
      ? translations.pages.product.scrubPantsFitTitle
      : translations.magnolia.reviewsPage.filters.filter.Fit.title

  return {
    labelText,
    labelDisplayValue: capitalize(displayValue),
    soldOutLabelPostfix,
    value,
    renderAsDisplayValue,
    setValue,
    error,
    hide,
    hasASizeInStock,
  }
}

type VariantSelectionFitRadioGroupProps = {
  callToActionColor?: React.CSSProperties['color']
} & VariantSelectionFieldProps

export const VariantSelectionFitRadioGroup = ({
  onChange,
  callToActionColor,
}: VariantSelectionFitRadioGroupProps) => {
  const { product, trackingCategory } = useContext(VariantSelectionFormContext)!
  const { hasASizeInStock, value, hide, labelText, labelDisplayValue, soldOutLabelPostfix } =
    useVariantSelectionFitField()

  return (
    <div style={{ display: hide ? 'none' : 'block' }}>
      <FormRadioGroup>
        <VariantSelectionFormGroupLabel>
          {labelText}{' '}
          {labelDisplayValue.length > 0 ? (
            <VariantSelectionFieldValueDisplay>
              {labelDisplayValue}
              {soldOutLabelPostfix}
            </VariantSelectionFieldValueDisplay>
          ) : null}
        </VariantSelectionFormGroupLabel>
        <VariantSelectionOptionsRow>
          {product.options.fits?.map(fitOption => {
            const fit = fitOption.fit
            const isSelected = value === fit.id
            const disabled = isSelected
              ? fitOption.outOfStock && !hasASizeInStock
              : fitOption.outOfStock
            return (
              <FormRadio
                name={'fit'}
                key={fit.id}
                value={fit.id}
                required
                render={htmlInputProps => {
                  return (
                    <label
                      htmlFor={htmlInputProps.id}
                      {...trackEvent({
                        action: 'click',
                        category: trackingCategory,
                        label: 'Fit',
                        value: fit.id,
                      })}
                    >
                      <VariantSelectionInvisibleInput
                        {...htmlInputProps}
                        aria-label={fit.displayValue}
                        onChange={changeEvent => {
                          htmlInputProps.onChange?.(changeEvent)
                          if (changeEvent.target.checked) {
                            onChange?.(fit.id)
                          }
                        }}
                      />
                      <VariantSelectionOption
                        aria-hidden={true}
                        aria-label={fit.displayValue}
                        isSelected={isSelected}
                        soldOut={disabled}
                        accentColor={callToActionColor}
                        as='div'
                      >
                        {capitalize(fit.displayValue)}
                      </VariantSelectionOption>
                    </label>
                  )
                }}
              />
            )
          })}
        </VariantSelectionOptionsRow>
        <FormError name='fit' />
      </FormRadioGroup>
    </div>
  )
}

export const VariantSelectionFitSelect = ({
  onChange: onChangeProp,
}: VariantSelectionFieldProps) => {
  const { product, trackingCategory } = useContext(VariantSelectionFormContext)!
  const { hide, labelText, setValue, renderAsDisplayValue, value, error } =
    useVariantSelectionFitField()
  const onChange = useCallback(
    (newValue: string) => {
      onChangeProp?.(newValue)
      setValue(newValue)
    },
    [onChangeProp, setValue]
  )

  return (
    <div style={{ display: hide ? 'none' : 'block' }}>
      <Select
        value={value}
        required
        onChange={onChange}
        name={'fit'}
        renderSelectValue={renderAsDisplayValue}
        isInvalid={!!error}
        heading={labelText}
      >
        {product.options.fits?.map(fitOption => {
          const fit = fitOption.fit
          return (
            <SelectOption
              key={fit.id}
              value={fit.id}
              {...trackEvent({
                action: 'click',
                category: trackingCategory,
                label: 'Fit',
                value: fit.id,
              })}
            >
              {renderAsDisplayValue(fit.id)}
            </SelectOption>
          )
        })}
      </Select>
      <FormError name='fit' />
    </div>
  )
}
